import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { getSiteConfig, type ContactPreferenceChannel } from '@shared/config';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

type ContactPreferencesApiResponse = {
  verticals?: {
    lottery?: boolean;
    casinoAndScratchcards?: boolean;
    sportsbook?: boolean;
  };
  notificationChannels?: {
    email?: boolean;
    sms?: boolean;
    phone?: boolean;
    post?: boolean;
    trustedPartners?: boolean;
  };
};

type ProductPreferenceValue = 'lottery' | 'casino' | 'sportsbook';

type ContactPreferencesData = {
  productPreference: string[];
  channels: string[];
  contactPreferences: ContactPreferencesApiResponse | undefined;
  productSelection: ProductPreferenceValue[];
  setProductSelection: (selected: ProductPreferenceValue[]) => void;
  channelSelection: ContactPreferenceChannel[];
  setChannelSelection: (selected: ContactPreferenceChannel[]) => void;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
};

function readSavedChannels(notificationChannels: ContactPreferencesApiResponse['notificationChannels']): ContactPreferenceChannel[] | null {
  if (notificationChannels === undefined) return null;

  const { availableChannels } = getSiteConfig().CONTACT_PREFERENCES;
  return availableChannels.filter(channel => notificationChannels[channel]);
}

function readSavedProducts(verticals: ContactPreferencesApiResponse['verticals']): ProductPreferenceValue[] | null {
  if (verticals === undefined) return null;

  const saved: ProductPreferenceValue[] = [];
  if (verticals.lottery) saved.push('lottery');
  if (verticals.casinoAndScratchcards) saved.push('casino');
  if (verticals.sportsbook) saved.push('sportsbook');
  return saved;
}

function useDraftSelection<T extends string>(saved: readonly T[] | null, isSheetOpen: boolean) {
  const savedKey = saved === null ? null : [...saved].sort().join('|');
  const [selection, setSelectionState] = useState<T[] | null>(null);
  const hasEdited = useRef(false);

  useEffect(() => {
    if (savedKey === null || hasEdited.current) return;
    setSelectionState(saved ? [...saved] : []);
    // The key is the saved values, so a refetch of the same values does not replace an edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);

  useEffect(() => {
    if (isSheetOpen) return;

    hasEdited.current = false;
    setSelectionState(null);
  }, [isSheetOpen]);

  const setSelection = (next: T[]) => {
    hasEdited.current = true;
    setSelectionState(next);
  };

  return {
    selection: selection ?? (saved ? [...saved] : []),
    setSelection,
  };
}

type UseContactPreferencesDataProps = {
  namespace: string;
  fallbackProductPreference?: string[];
  fallbackChannels?: string[];
  isProductPreferencesSheetOpen?: boolean;
  isChannelsSheetOpen?: boolean;
};

function useContactPreferencesData({
  namespace,
  fallbackProductPreference = [],
  fallbackChannels = [],
  isProductPreferencesSheetOpen = false,
  isChannelsSheetOpen = false,
}: UseContactPreferencesDataProps): ContactPreferencesData {
  const t = useTranslations(namespace);

  // Fetch contact preferences (requires credentials to pass auth cookie)
  const {
    data: contactPreferences,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useApiQuery<ContactPreferencesApiResponse>(API_ROUTES.MY_ACCOUNT.CONTACT_PREFERENCES.GET, { credentials: true });

  const savedProducts = readSavedProducts(contactPreferences?.verticals);
  const { selection: productSelection, setSelection: setProductSelection } = useDraftSelection(savedProducts, isProductPreferencesSheetOpen);

  const savedChannels = readSavedChannels(contactPreferences?.notificationChannels);
  const { selection: channelSelection, setSelection: setChannelSelection } = useDraftSelection(savedChannels, isChannelsSheetOpen);

  // Map API response into display arrays
  const productPreference = (() => {
    const labelMap: Record<string, string> = {
      lottery: t('contact_preferences.products.labels.lotteries') || 'Lotteries',
      casino: t('contact_preferences.products.labels.casino_live') || 'Casino & Live Casino',
      sportsbook: t('contact_preferences.products.labels.sportsbook') || 'Sportsbook',
    };

    if (contactPreferences?.verticals === undefined) {
      return fallbackProductPreference.map(code => {
        const lower = code.toLowerCase();
        return labelMap[lower] ?? code;
      });
    }

    const enabled: string[] = [];
    if (contactPreferences.verticals.lottery) enabled.push(labelMap.lottery!);
    if (contactPreferences.verticals.casinoAndScratchcards) enabled.push(labelMap.casino!);
    if (contactPreferences.verticals.sportsbook) enabled.push(labelMap.sportsbook!);
    return enabled; // when API present but all false, return [] to show "Not set"
  })();

  const channels = (() => {
    const notificationChannels = contactPreferences?.notificationChannels;
    if (notificationChannels === undefined) return fallbackChannels;

    const { availableChannels } = getSiteConfig().CONTACT_PREFERENCES;
    return availableChannels.filter(channel => notificationChannels[channel]); // when API present but all false, return []
  })();

  return {
    productPreference,
    channels,
    contactPreferences: contactPreferences || undefined,
    productSelection,
    setProductSelection,
    channelSelection,
    setChannelSelection,
    isLoading,
    isFetching,
    isError,
    refetch: () => void refetch(),
  };
}

export { useContactPreferencesData };
export type { ContactPreferencesApiResponse };
